import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-8">Terms of Service</h1>
        <p className="text-gray-400 text-sm mb-8">Last updated: March 9, 2026</p>

        <div className="space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using deepnets.ai (the "Service"), operated by Percipio LLC ("we", "us", or "our"),
              you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Description of Service</h2>
            <p>
              Deepnets.ai provides blockchain token analysis, holder research, wallet network mapping, and related
              informational tools for the Solana ecosystem. The Service analyzes publicly available on-chain data
              and presents it in an accessible format.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Not Financial Advice</h2>
            <p>
              <strong className="text-white">The Service is for informational purposes only.</strong> Nothing on
              deepnets.ai constitutes financial advice, investment advice, trading advice, or any other kind of
              professional advice. You should not treat any of the Service's content as such.
            </p>
            <p className="mt-2">
              Risk scores, safety assessments, and analysis results are generated algorithmically and may be
              incomplete, inaccurate, or outdated. Always do your own research (DYOR) before making any
              trading or investment decisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Accounts and Authentication</h2>
            <p>
              To access certain features, you must connect a Solana wallet. By connecting your wallet, you
              authenticate your identity through a cryptographic signature. You are responsible for maintaining
              the security of your wallet and any activity that occurs under your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Credits</h2>
            <p>
              Certain features of the Service require credits. New accounts may receive a complimentary credit
              balance. Credits are non-transferable, non-refundable, and have no cash value. We reserve the right
              to modify credit pricing, availability, and the features that require credits at any time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Scrape, crawl, or use automated tools to extract data at scale without permission</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Impersonate another person or entity</li>
              <li>Use the Service to manipulate markets or facilitate fraud</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Intellectual Property</h2>
            <p>
              The Service, including its design, code, analysis algorithms, and original content, is owned by
              Percipio LLC. On-chain data analyzed by the Service is publicly available and not claimed as our
              intellectual property.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Percipio LLC shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred
              directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
              <li>Your use or inability to use the Service</li>
              <li>Any errors, inaccuracies, or omissions in the Service's analysis or data</li>
              <li>Any trading or investment decisions made based on information from the Service</li>
              <li>Unauthorized access to or alteration of your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Disclaimer of Warranties</h2>
            <p>
              The Service is provided "as is" and "as available" without warranties of any kind, either express
              or implied, including but not limited to implied warranties of merchantability, fitness for a
              particular purpose, and non-infringement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Modifications</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be posted on this page with
              an updated date. Your continued use of the Service after changes constitutes acceptance of the
              revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without cause, and
              with or without notice. Upon termination, any unused credits will be forfeited.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Contact</h2>
            <p>
              Questions about these Terms? Reach out via{' '}
              <a
                href="mailto:support@deepnets.ai"
                className="text-crypto-accent hover:text-crypto-accent/80 transition-colors"
              >
                support@deepnets.ai
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800 flex justify-between">
          <Link to="/" className="text-crypto-accent hover:text-crypto-accent/80 transition-colors">
            ← Back to home
          </Link>
          <Link to="/privacy" className="text-blue-400 hover:text-blue-300">
            Privacy Policy →
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
